//----REACT libraries
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
//----ANT Design
import { Row, Col, Card, Grid } from "antd";
import {
  HistoryOutlined,
  NumberOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
//----Project specific
import "./styles.css";
import { getHeartBeatResult } from "apis/eolApis";
import { heartBeatFactors } from "./heartBeatFactors";
import { formatDateAndTime } from "components/Common/Utils";
import { setNavStop } from "reducers/authSlice";
import {
  setHBPageMini,
  setHBResultDetails,
  setHBTestRunningStatus,
} from "reducers/eolTestsSlice";
import Loading from "components/Common/Loading";
import APIError from "components/Common/APIError";
import NoData from "components/Common/NoData";
import EOLProgressIcon from "./EOLProgressIcon";
import HBDataLink from "./HBDataLink";

const { useBreakpoint } = Grid;

const HBResultPanel = ({ showCommentModal }) => {
  const dispatch = useDispatch();
  const { xl, xxl } = useBreakpoint();
  const eol = useSelector((state) => state.eolTests);
  const vehicleSlice = useSelector((state) => state.eol);
  const vIdVal = vehicleSlice?.vehicleDetails?.vehId;
  const HBTestRunningStatus = eol?.HBTestRunningStatus;
  const HBResultDetails = eol?.HBResultDetails;
  const selectedHBTestId =
    eol?.selectedHBTestId || eol?.HBResultDetails?.testId;

  const iconSize = xl && !xxl ? 60 : 84;

  const {
    data: hbResultData,
    isLoading: hbResultLoading,
    isError: hbResultIsError,
    error: hbResultError,
  } = useQuery({
    queryKey: ["hbresult", selectedHBTestId],
    keepPreviousData: true,
    queryFn: () => getHeartBeatResult(selectedHBTestId),
    enabled: !!(vIdVal && selectedHBTestId),
    staleTime: HBTestRunningStatus ? 0 : 1 * 10 * 60 * 1000, // If test is running cache is 10s, otherwise it is 10m
    // refetchOnMount: true,
    refetchInterval: () => (HBTestRunningStatus ? 10 * 1000 : 0), // If test is running refresh every 10 secs
  });

  useEffect(() => {
    dispatch(setHBResultDetails(hbResultData));

    if (HBTestRunningStatus && hbResultData?.status === "COMPLETED") {
      dispatch(setHBPageMini(1));
      showCommentModal();
      dispatch(setHBTestRunningStatus(false));
      dispatch(setNavStop(false));
    }
    // eslint-disable-next-line
  }, [HBTestRunningStatus, hbResultData, dispatch]);

  useEffect(() => {
    dispatch(setNavStop(false));
  }, [hbResultError, dispatch]);

  const connectivityResults = HBResultDetails?.data?.connectivity;

  const availabiltyResultList = heartBeatFactors.availabilty.map(
    (availTest) => {
      const testResult = connectivityResults?.filter(
        (test) => test.name === availTest.index
      );
      return {
        item: availTest.text,
        dataTable: availTest.table,
        value: testResult?.[0]?.value ? testResult[0].value : "N/A",
        result: testResult?.[0]?.value ? testResult[0].result : null,
        unit: testResult?.[0]?.value ? testResult[0].unit : "",
        field: [],
        type: "circle",
        percent: testResult?.[0].value === null ? 0 : testResult?.[0].value,
        size: iconSize,
        showErrors: true,
      };
    }
  );

  const rangeResultList = heartBeatFactors.range.map((rangeTest) => {
    const testResult = connectivityResults?.filter(
      (test) => test.name === rangeTest.index
    );
    return {
      item: rangeTest.text,
      dataTable: rangeTest.table,
      value: testResult?.[0]?.value ? testResult[0].value : "N/A",
      result: testResult?.[0].result,
      unit: testResult?.[0]?.value ? testResult[0].unit : "",
      field: rangeTest.field,
      type: "circle",
      percent: 100,
      size: iconSize,
      showErrors: false,
    };
  });

  const gridStyle = {
    width: "100%",
    display: "flex",
    gap: "12px",
    justifyContent: "space-between",
    flexDirection: "row",
    background: "#FFF",
    padding: "20px 12px",
    margin: "8px",
    alignItems: "center",
    borderRadius: "6px",
    boxShadow: "0px 20px 50px 0px rgba(0, 0, 0, 0.07)",
  };

  return (
    <>
      <Row gutter={[16, 16]} className="h100">
        <Col span={24}>
          <Row gutter={[16, 16]} className="h100">
            <Col span={24}>
              <Card
                className="h100"
                title="Heart Beat Result"
                extra={
                  HBResultDetails?.startTime && (
                    <div className="fs13">
                      <NumberOutlined />
                      <span className="card-sub-text mr05">
                        {HBResultDetails?.iteration}
                      </span>
                      <HistoryOutlined />
                      <span className="card-sub-text ml05">
                        {formatDateAndTime(HBResultDetails?.startTime)}
                      </span>
                    </div>
                  )
                }
                styles={{
                  body: {
                    padding: "4px",
                    minHeight: "270px",
                  },
                }}
              >
                {hbResultLoading && <Loading />}
                {hbResultIsError && (
                  <APIError message={hbResultError.message} />
                )}
                {!hbResultLoading && !hbResultData?.data && <NoData />}
                <Row gutter={[16, 16]} className="w100">
                  {!hbResultIsError &&
                    hbResultData?.data &&
                    availabiltyResultList?.map((testItem, index) => {
                      return (
                        <Col
                          key={index}
                          span={24}
                          sm={{ span: 12 }}
                          xl={{ span: 6 }}
                        >
                          <Card.Grid style={gridStyle}>
                            <div
                              className="grid-text-box"
                              style={{ background: "#fff", width: "130px" }}
                            >
                              <div className="grid-result-text">
                                {testItem.item}
                              </div>
                              <div className="grid-result-percentage">{`${testItem.value} ${testItem.unit}`}</div>
                              <div className="grid-data-link-box">
                                <HBDataLink
                                  vIdVal={vIdVal}
                                  dataTable={testItem.dataTable}
                                  field={testItem.field}
                                  testId={selectedHBTestId}
                                  showErrors={testItem.showErrors}
                                />
                              </div>
                            </div>
                            <div className="grid-progress-box">
                              {HBTestRunningStatus ? (
                                <Loading3QuartersOutlined
                                  style={{
                                    fontSize: testItem.size,
                                    animationDuration: "5s",
                                    color: "#F4F4F4",
                                  }}
                                  spin
                                />
                              ) : (
                                <EOLProgressIcon
                                  percent={testItem.percent}
                                  showInfo={HBTestRunningStatus ? false : true}
                                  size={testItem.size}
                                  status={testItem.result}
                                />
                              )}
                            </div>
                          </Card.Grid>
                        </Col>
                      );
                    })}
                </Row>
                <Row gutter={[16, 16]} className="w100">
                  {!hbResultIsError &&
                    hbResultData?.data &&
                    rangeResultList?.map((testItem, index) => {
                      return (
                        <Col
                          key={index}
                          span={24}
                          sm={{ span: 12 }}
                          xl={{ span: 6 }}
                        >
                          <Card.Grid key={index} style={gridStyle}>
                            <div
                              className="grid-text-box"
                              style={{ background: "#fff", width: "130px" }}
                            >
                              <div className="grid-result-text">
                                {testItem.item}
                              </div>
                              <div className="grid-result-percentage">{`${testItem.value} ${testItem.unit}`}</div>
                              <div className="grid-data-link-box">
                                <HBDataLink
                                  vIdVal={vIdVal}
                                  dataTable={testItem.dataTable}
                                  field={testItem.field}
                                  testId={selectedHBTestId}
                                  showErrors={testItem.showErrors}
                                />
                              </div>
                            </div>
                            <div className="grid-progress-box">
                              {HBTestRunningStatus ? (
                                <Loading3QuartersOutlined
                                  style={{
                                    fontSize: testItem.size,
                                    animationDuration: "5s",
                                    color: "#F4F4F4",
                                  }}
                                  spin
                                />
                              ) : (
                                <EOLProgressIcon
                                  percent={testItem.percent}
                                  showInfo={HBTestRunningStatus ? false : true}
                                  size={testItem.size}
                                  status={testItem.result}
                                />
                              )}
                            </div>
                          </Card.Grid>
                        </Col>
                      );
                    })}
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default HBResultPanel;
